import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import AnimeCard from "@/components/feature/AnimeCard";
import EmptyState from "@/components/base/EmptyState";
import { useSeo } from "@/hooks/useSeo";
import { animes } from "@/mocks/anime";
import { newsList } from "@/mocks/news";

type ResultTab = "anime" | "news";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = searchParams.get("q") || "";
  const initialTab = searchParams.get("type") === "news" ? "news" : "anime";
  const [tab, setTab] = useState<ResultTab>(initialTab);
  const [draft, setDraft] = useState(keyword);

  useSeo({
    title: keyword ? `搜索“${keyword}”结果 - 樱花动漫官网` : "站内搜索 - 樱花动漫官网",
    description: keyword
      ? `樱花动漫官网搜索“${keyword}”的结果，包含相关动漫作品与资讯内容。`
      : "在樱花动漫官网搜索动漫作品与资讯，支持动漫、资讯两类结果切换。",
    keywords: "站内搜索,动漫搜索,资讯搜索,樱花动漫官网",
  });

  useEffect(() => {
    setDraft(keyword);
  }, [keyword]);

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  const matchedAnime = useMemo(() => {
    const value = keyword.trim();
    if (!value) return [];
    return animes.filter(
      (item) =>
        item.title.includes(value) ||
        item.subtitle.includes(value) ||
        item.genres.some((genre) => genre.includes(value)) ||
        item.tags.some((tag) => tag.includes(value)),
    );
  }, [keyword]);

  const matchedNews = useMemo(() => {
    const value = keyword.trim();
    if (!value) return [];
    return newsList.filter(
      (item) =>
        item.title.includes(value) ||
        item.excerpt.includes(value) ||
        item.tags.some((tag) => tag.includes(value)) ||
        item.category.includes(value),
    );
  }, [keyword]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value) return;
    const next = new URLSearchParams();
    next.set("q", value);
    next.set("type", tab);
    setSearchParams(next);
  };

  const changeTab = (next: ResultTab) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    params.set("type", next);
    setSearchParams(params);
  };

  const total = matchedAnime.length + matchedNews.length;
  const hasKeyword = keyword.trim().length > 0;

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-9 md:px-8 md:py-11">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="text-foreground-800">站内搜索</span>
            </nav>

            <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">
              {hasKeyword ? (
                <>
                  搜索“<span className="text-primary-600">{keyword}</span>”的结果
                </>
              ) : (
                "站内搜索"
              )}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              {hasKeyword
                ? `在全部动漫与资讯中共找到 ${total} 条结果，可切换分类查看。`
                : "输入动漫名称、类型或资讯关键词，快速找到想看的内容。"}
            </p>

            <form onSubmit={handleSubmit} className="mt-5 flex max-w-2xl flex-col gap-2 sm:flex-row">
              <div className="flex h-11 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 transition-colors focus-within:border-primary-400">
                <i className="ri-search-line text-base text-foreground-400" />
                <input
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder="例如：奇幻、葬送的芙莉莲、声优"
                  aria-label="搜索关键词"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:py-0"
              >
                搜索
              </button>
            </form>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <span>热门搜索：</span>
              {["奇幻", "悬疑", "国产动画", "新番"].map((item) => (
                <Link
                  key={item}
                  to={`/search?q=${encodeURIComponent(item)}`}
                  className="rounded-md bg-background-50 px-2 py-1 text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  {item}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8">
          <div className="flex flex-wrap items-center gap-1 border-b border-background-200">
            <button
              type="button"
              onClick={() => changeTab("anime")}
              className={[
                "relative whitespace-nowrap px-4 py-3 text-sm transition-colors",
                tab === "anime" ? "font-medium text-primary-600" : "text-foreground-600 hover:text-foreground-900",
              ].join(" ")}
            >
              动漫作品
              <span className="ml-1.5 rounded-full bg-background-200 px-1.5 py-0.5 font-label text-[10px] text-foreground-700">
                {matchedAnime.length}
              </span>
              {tab === "anime" && <span className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-primary-500" />}
            </button>
            <button
              type="button"
              onClick={() => changeTab("news")}
              className={[
                "relative whitespace-nowrap px-4 py-3 text-sm transition-colors",
                tab === "news" ? "font-medium text-primary-600" : "text-foreground-600 hover:text-foreground-900",
              ].join(" ")}
            >
              资讯内容
              <span className="ml-1.5 rounded-full bg-background-200 px-1.5 py-0.5 font-label text-[10px] text-foreground-700">
                {matchedNews.length}
              </span>
              {tab === "news" && <span className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-primary-500" />}
            </button>
          </div>

          <div className="mt-7">
            {!hasKeyword ? (
              <EmptyState
                title="还没有输入搜索词"
                description="试试输入动漫名称、类型或标签，例如「奇幻」「葬送的芙莉莲」「声优」。"
                secondaryLabel="浏览动漫分类"
                secondaryTo="/category"
              />
            ) : tab === "anime" ? (
              matchedAnime.length > 0 ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {matchedAnime.map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="没有找到相关的动漫"
                  description={`没有与“${keyword}”匹配的作品。换个更短的关键词，或者直接去分类页浏览。`}
                  actionLabel="重新搜索"
                  onAction={() => navigate("/search")}
                  secondaryLabel="浏览全部动漫"
                  secondaryTo="/category"
                />
              )
            ) : matchedNews.length > 0 ? (
              <ul className="divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
                {matchedNews.map((item) => (
                  <li key={item.id}>
                    <Link to={`/news/${item.id}`} className="group flex flex-col gap-3 p-4 sm:flex-row sm:items-center md:p-5">
                      <div className="h-20 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-20 sm:w-32">
                        <img
                          src={item.cover}
                          alt={`${item.title} 封面`}
                          className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded bg-secondary-100 px-1.5 py-0.5 font-label text-[10px] text-secondary-900">
                            {item.category}
                          </span>
                          <span className="font-label text-[11px] text-foreground-500">{item.date}</span>
                        </div>
                        <h3 className="mt-1.5 line-clamp-1 text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                          {item.title}
                        </h3>
                        <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{item.excerpt}</p>
                      </div>
                      <i className="ri-arrow-right-line hidden shrink-0 text-foreground-400 sm:block" />
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                title="没有找到相关的资讯"
                description={`没有与“${keyword}”匹配的资讯内容。可以切换回动漫作品看看，或者换个关键词。`}
                actionLabel="看看动漫作品"
                onAction={() => changeTab("anime")}
                secondaryLabel="进入资讯频道"
                secondaryTo="/news"
              />
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}